import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import FarmersNav from "@/components/FarmersNav";
import { fpPath } from "@/lib/site";
import { toolBySlug, TOOLKIT } from "@/data/farmerToolkit";
import PhoneMock from "@/components/toolkit/PhoneMock";
import { ArrowLeft, ArrowRight, CheckCircle, Sparkle } from "@phosphor-icons/react";

/**
 * /farmers/toolkit/:slug — Per-tool preview.
 * Sections: hero, "what it does", "how it works", the two mockups,
 * next tool nav.
 */
export default function FarmerToolPreview() {
  const { slug } = useParams();
  const tool = toolBySlug(slug);
  if (!tool) return <Navigate to={fpPath("/toolkit")} replace />;

  const idx = TOOLKIT.findIndex((t) => t.slug === slug);
  const prev = idx > 0 ? TOOLKIT[idx - 1] : null;
  const next = idx < TOOLKIT.length - 1 ? TOOLKIT[idx + 1] : null;

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid={`tool-preview-${tool.slug}`}>
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>

      <div className="max-w-6xl mx-auto mt-6">
        <Link
          to={fpPath("/toolkit")}
          className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          data-testid="tool-preview-back"
        >
          <ArrowLeft size={11} weight="bold" /> All 12 tools
        </Link>

        {/* Hero + first mockup side-by-side */}
        <section className="mt-4 grid md:grid-cols-[1fr_auto] gap-8 items-start">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              Tool {idx + 1} · Stage {tool.stage}
              <span className="ml-2 text-[#3A3830]/60">· {statusLabel(tool.status)}</span>
            </div>
            <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight text-[#151312]">
              {tool.name}
            </h1>
            <p className="mt-3 font-serif-display text-xl text-[color:var(--np-brand,#B85F2A)] italic">{tool.tagline}</p>
            <p className="mt-4 max-w-2xl text-[#3A3830]">{tool.elevator}</p>

            {tool.deepLink && (
              <Link
                to={fpPath(tool.deepLink)}
                className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
                data-testid="tool-preview-open"
              >
                Open the tool <ArrowRight size={11} weight="bold" />
              </Link>
            )}
          </div>

          <div className="hidden md:block">
            <PhoneMock data={tool.mocks[0]} scale="lg" label={`Screen 1 of 2 · ${tool.name}`} />
          </div>
        </section>

        {/* What it does */}
        <section className="mt-14 grid md:grid-cols-[1fr_1fr] gap-8">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              <Sparkle size={11} weight="fill" className="inline mr-1" /> What this tool does
            </div>
            <ul className="mt-3 space-y-2">
              {tool.what.map((line) => (
                <li key={line} className="flex items-start gap-2 text-[#1F1D1A]">
                  <CheckCircle size={16} weight="fill" className="mt-1 text-[color:var(--np-brand,#B85F2A)] shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Mobile-only first mockup */}
          <div className="md:hidden flex justify-center">
            <PhoneMock data={tool.mocks[0]} scale="md" label={`Screen 1 of 2`} />
          </div>

          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              How it works
            </div>
            <ol className="mt-3 space-y-3">
              {tool.how.map((step, i) => (
                <li key={i} className="rounded-2xl border border-black/10 bg-white p-4">
                  <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A]">Step {i + 1}</div>
                  <div className="mt-1 font-serif-display text-lg text-[#151312] leading-tight">{step.title}</div>
                  <p className="mt-1 text-sm text-[#3A3830]">{step.body || step.value}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Second mockup — full width */}
        <section className="mt-16 rounded-3xl bg-[#151312] text-[#F5EBDC] p-6 md:p-10" data-testid="tool-preview-second-mock">
          <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                Screen 2 · after the flow
              </div>
              <h2 className="mt-1 font-serif-display text-3xl md:text-4xl leading-tight">{tool.mocks[1].title}</h2>
              {tool.mocks[1].subtitle && (
                <p className="mt-2 max-w-lg text-[#E7DDCA]">{tool.mocks[1].subtitle}</p>
              )}
            </div>
            <PhoneMock data={tool.mocks[1]} scale="lg" />
          </div>
        </section>

        {/* Full feature list — the detailed "what the farmer gets" breakdown */}
        {tool.features && (
          <section className="mt-16" data-testid="tool-preview-features">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              <Sparkle size={11} weight="fill" className="inline mr-1" /> Everything this tool gives you
            </div>
            <h2 className="mt-1 font-serif-display text-3xl md:text-4xl leading-tight text-[#151312]">
              {tool.features.lead}
            </h2>
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              {tool.features.groups.map((g, gi) => (
                <div key={gi} className="rounded-2xl border border-black/10 bg-white p-5" data-testid={`tool-feature-group-${gi}`}>
                  {g.label && (
                    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
                      {g.label}
                    </div>
                  )}
                  <ul className="space-y-2">
                    {g.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#1F1D1A] leading-snug">
                        <CheckCircle size={14} weight="fill" className="mt-0.5 text-[color:var(--np-brand,#B85F2A)] shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Prev / next tool */}
        <nav className="mt-14 flex flex-wrap items-center justify-between gap-4" data-testid="tool-preview-nav">
          {prev ? (
            <Link
              to={fpPath(`/toolkit/${prev.slug}`)}
              className="inline-flex items-center gap-2 rounded-2xl border border-black/10 bg-white px-4 py-3 hover:border-[color:var(--np-brand,#B85F2A)] transition"
            >
              <ArrowLeft size={14} weight="bold" className="text-[color:var(--np-brand,#B85F2A)]" />
              <span>
                <span className="block font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A]">Previous</span>
                <span className="block font-serif-display text-sm text-[#151312]">{prev.name}</span>
              </span>
            </Link>
          ) : <span />}

          {next && (
            <Link
              to={fpPath(`/toolkit/${next.slug}`)}
              className="ml-auto inline-flex items-center gap-2 rounded-2xl border border-black/10 bg-white px-4 py-3 hover:border-[color:var(--np-brand,#B85F2A)] transition"
              data-testid="tool-preview-next"
            >
              <span className="text-right">
                <span className="block font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A]">Next</span>
                <span className="block font-serif-display text-sm text-[#151312]">{next.name}</span>
              </span>
              <ArrowRight size={14} weight="bold" className="text-[color:var(--np-brand,#B85F2A)]" />
            </Link>
          )}
        </nav>
      </div>
    </main>
  );
}

function statusLabel(status) {
  if (status === "shipping") return "Shipping now";
  if (status === "building") return "In build";
  return "Preview";
}
